/**
 * @class msysMessage_filter
 * @description
 * Provides function to generate JavaScript function for message filtering
 *
 * Each filter is array of rules.
 * Each rule is array [action,conditions...] resp. [action,conoditions...,operation]
 *
 * Actions that controls filtering, i.e. control flow of the evaluation are:
 * SKIP: if all conditions satisfies, filter will return null
 * KEEP: if any of conditions dissatisfies, filter will return null
 * APPROVE: if all conditions satisfies, filter will return masked parameters so far
 *
 * Actions that control masking, i.e. change of parameters are:
 * DELETE: if all conditions satisfies, will delete parameters as specified in last rule array member; single string or array of strings
 * REPLACE: if all conditions satisfies, will replace parameters as specified in last rule array member; single string or array of strings
 *
 * Condition has following format:
 * VariableOperatorValue
 * Variable is definition of variable to test, e.g.
 * Operator is one of:
 *   = matches if variable equals to value
 *   ! matches if variable does not equal to value
 *   : matches if variable equals to one of values, where values are separated by comma, e.g. action:mmH264,mmExit   (not comma is used as separator, so it can not be used in values)
 *   ? matches if variable is neither null, undefined or empty string
 *   ~ matches variable against regular expression, e.g. action~mmUser\\w+
 *   !~ matches if variable does not match regular expression, e.g. action!mmUser\\w+
 *   < <= > >= compares variable against value using corresponding JavaScript operator
 * Value is javascript literal resp. regular expression literal (for ~ and !~)
 *
 * Operation is last member of rule array and is either single string or array of operation strings in following format:
 *   variable=value will replace variable with given value
 *   variable~matching~replacement will replace variable using regular expression like variable.replace(new RegExp(matching), replacement)
 *   variable~matching~replacement~flags will replace variable using regular expression like variable.replace(new RegExp(matching,flags), replacement)
 *
 */

const filters = require('./filters.js')
module.exports = { filters, createFilterFunction }

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Constructs javascript function that will be able to validate set of filtering rules
 * for Mosy message.
 * result is: function(sender, target, action, params)
 * @param {array} filter - filtering criteria
 * @param {array} immutable - if set to true, will automatically create copy of params to prevent mutation of given params
 */
function createFilterFunction(filter, immutable) {
  const re = /^((?:params)?(?:\[\d*\]|\{\w*\}|\.\w+)+|sender(?:\.\w+)?|target(?:\.\w+)?|action)(?:(\?|=|!~|!|~|<=|<|>=|>|:)(.*))?$/
  function str(str) {
    if(str.length===0) return 'null'
    else return JSON.stringify(str)
  }

  const variables = {}
  function ensureParentPath(path) {
    if(variables[path]) return [variables[path], '']
    const m = /^(.*)\.(\w+)$/.exec(path)
    if(!m) {
      const name = variables[path] = 'p'+Object.keys(variables).length
      return [name, `const ${name} = ${path} && typeof ${path}=='object' ? ${path} : []\n`]
    }else{
      const [parent_name, parent_addition] = ensureParentPath(m[1])
      const name = variables[path] = 'p'+Object.keys(variables).length
      const myself = parent_name + (/^\d+$/.test(m[2]) ? `[${m[2]}]` : `.${m[2]}`)
      return [name, parent_addition+`const ${name} = ${myself} && typeof ${myself}=='object' ? ${myself} : []\n`]
    }
  }

  if(!Array.isArray(filter)) return
  let def = ''
  let final = false
  let mutation_can_occur = false
  filter = filter.slice() // needed as we may change filter when include occurs
  let sendertxt_needed = 0
  let targettxt_needed = 0
  while(filter.length>0) {
    const rule = filter.shift()
    if(!Array.isArray(rule)) continue
    const action = String(rule[0]).toUpperCase()
    if(action==='INCLUDE') {
      if(Array.isArray(filters[rule[1]])) filter = [...filters[rule[1]], ...filter]
      continue
    }
    if(!/^SKIP|KEEP|APPROVE|DELETE|REPLACE$/.test(action)) continue

    const conditions = []
    let condition_variables = ''
    // for DELETE and REPLACE, last operand is the subject of operation
    const operation = /^DELETE|REPLACE$/.test(action) ? rule[rule.length-1] : null
    for(const cond of rule.slice(1, operation?-1:undefined)) {
      const m = re.exec(cond)
      if(!m) continue
      let variable = m[1].replace(/\[(\d+)\]/g, '.$1').replace(/\{(\w+)\}/g, '.$1').replace(/\[\]/g, '.@').replace(/\{\}/g, '.*')
      if(variable[0]==='.') variable = 'params'+variable // .param is shortcut for params.param
      if(variable==='sender') { variable='sendertxt'; sendertxt_needed=1 }
      if(variable==='target') { variable='targettxt'; targettxt_needed=1 }
      const operator = m[2]
      const value = m[3]

      // this will replace parent part with parent variable to ensure we can access the property
      variable = variable.replace(/^([^@*]*)\.(.+)$/, (m, p1, p2)=>{
        const [parent, definition] = ensureParentPath(p1)
        condition_variables += definition
        if(/^\d+$/.test(p2)) return parent+'['+p2+']'
        else return parent+'.'+p2
      })
      if(/[@*]/.test(variable)) {
        // TODO: add treatment of conditions matching iterative patterns
        continue
      }else{
        // single value
      }

      if(operator==='~') {
        conditions.push(`/${value}/.test(${variable})`)
      }else if(operator==='!~') {
        conditions.push(`!/${value}/.test(${variable})`)
      }else if(operator===':') {
        conditions.push(`/^(${value.split(',').join('|')})$/.test(${variable})`)
      }else if(operator==='?') {
        conditions.push(`${variable}!==null && typeof(${variable})!='undefined' && (typeof(${variable})!='string' || ${variable}.length>0)`)
      }else if(operator==='=') {
        conditions.push(`${variable}==${str(value)}`)
      }else if(operator==='!') {
        conditions.push(`${variable}!=${str(value)}`)
      }else if(operator==='<' || operator==='<=' || operator==='>' || operator==='>=') {
        conditions.push(`${variable}${operator}${str(value)}`)
      }else {
        continue
      }
    }
    def += condition_variables
    const condition_string = conditions.length===0 ? '' : `if(${conditions.join(' && ')}) `

    // for DELETE AND REPLACE we change the subject of operation
    if(operation) {
      const operations = Array.isArray(operation) ? operation : [operation]
      for(const operation of operations) {
        const conditions_copy = [...conditions]
        const m = re.exec(operation)
        if(!m) continue
        let variable = m[1].replace(/\[(\d+)\]/g, '.$1').replace(/\{(\w+)\}/g, '.$1').replace(/\[\]/g, '.@').replace(/\{\}/g, '.*')
        if(variable[0]==='.') variable = 'params'+variable // .param is shortcut for params.param
        const operator = m[2]
        const value = m[3]

        // this will replace parent part with parent variable to ensure we can access the property
        variable = variable.replace(/^([^@*]*)\.(.+)$/, (m, p1, p2)=>{
          const [parent, definition] = ensureParentPath(p1)
          def += definition
          if(/^\d+$/.test(p2)) return parent+'['+p2+']'
          else return parent+'.'+p2
        })
        if(/^\w+\.[@*]$/.test(variable)) {
          // special treatment to delete/replace all array/hash members
          mutation_can_occur = !!immutable
          const [, parent, loop_type] = /^(\w+)\.([@*])$/.exec(variable)
          if(action==='DELETE') def += condition_string + (loop_type==='@' ? `{ if(Array.isArray(${parent}) { s(); ${parent}.length=0 } }\n` : `{ if(${parent} && typeof(${parent})=='object') { s(); for(let x in ${parent}) delete ${parent}[x] } }\n`)
        }else if(/^\w+\.[@*]\.\w+$/.test(variable)) {
          // special treatment to delete/replace all array members' field
          mutation_can_occur = !!immutable
          let [, parent, loop_type, field] = /^(\w+)\.([@*])\.(\w+)$/.exec(variable)
          const access = /^\d+$/.test(field) ? `[${field}]` : `.${field}`
          field = /^\d+$/.test(field) ? field : `"${field}"`
          const loop = loop_type==='@' ? `for(let x of ${parent})` : `for(let x of Object.values(${parent}))`
          if(action==='DELETE') {
            def += condition_string + `${loop}{ if(x && typeof(x)=='object') { s(); delete x${access} } }\n`
          }else if(action==='REPLACE' && operator==='=') {
            let value_string
            if(/^\[.*\]$|^\{.*\}$/.test(value)) {
              try{
                value_string = JSON.stringify(JSON.parse(value))
              }catch(e) {
                value_string = JSON.stringify(value)
              }
            }else{
              value_string = JSON.stringify(value)
            }
            value_string = value_string.replace(/\$\{(\.\w+)\}/g, (m, p1)=>`"+(x${access}!==null && typeof x${access}!='undefined' ? x${access}${p1} : "")+"`)
            value_string = value_string.replace(/^"(\d+)"$/, '$1')
            def += condition_string + `${loop}{ if(x && typeof(x)=='object' && ${field} in x) { s(); x${access}=${value_string} } }\n`
          }else if(action==='REPLACE' && operator==='~') {
            const m = /^(.*?)~(.*?)(?:~(\w*))?$/.exec(value)
            if(m) {
              def += condition_string + `${loop}{ if(x && typeof(x)=='object' && ${field} in x) { s(); x${access}=String(x${access}).replace(new RegExp(${JSON.stringify(m[1])}, ${JSON.stringify(m[3]||'')}), ${JSON.stringify(m[2])}) } }\n`
            }
          }
        }else if(/[@*]/.test(variable)) {
          // TODO: add treatment of operations on expressions with multiple iterative parts
          mutation_can_occur = !!immutable
          continue
        }else{
          // single value
          const m = /^(\w+)\.(\w+)$/.exec(variable)
          if(!m) {
            console.error(`Invalid variable in operation ${operation} in filter action ${action}`)
            continue
          }
          const [, parent, field] = m

          mutation_can_occur = !!immutable
          conditions_copy.push(`"${field}" in ${parent}`)
          const condition_string = `if(${conditions_copy.join(' && ')})`
          if(action==='DELETE') def += condition_string + `{ s(); delete ${variable} }\n`
          else if(action==='REPLACE' && operator==='=') {
            let value_string
            if(/^\[.*\]$|^\{.*\}$/.test(value)) {
              try{
                value_string = JSON.stringify(JSON.parse(value))
              }catch(e) {
                value_string = JSON.stringify(value)
              }
            }else{
              value_string = JSON.stringify(value)
            }
            value_string = value_string.replace(/\$\{(\.\w+)\}/g, (m, p1)=>`"+(${variable}!==null && typeof ${variable}!='undefined' ? ${variable}${p1} : "")+"`)
            value_string = value_string.replace(/^"(\d+)"$/, '$1')
            def += condition_string + `{ s(); ${variable}=${value_string} }\n`
          }else if(action==='REPLACE' && operator==='~') {
            const m = /^(.*?)~(.*?)(?:~(\w*))?$/.exec(value)
            if(m) {
              def += condition_string + `{ s(); ${variable}=String(${variable}).replace(new RegExp(${JSON.stringify(m[1])}, ${JSON.stringify(m[3]||'')}), ${JSON.stringify(m[2])}) }\n`
            }
          }
        }
      }
    }else{
      if(action==='SKIP') def += condition_string + 'FINISH_FALSE\n'
      else if(action==='APPROVE') def += condition_string + 'FINISH_TRUE\n'
      else if(action==='KEEP' && conditions.length>0) def += `if(!(${conditions.join(' && ')})) FINISH_FALSE\n`
      if(conditions.length===0 && action!=='KEEP') {
        final = true
        break
      }
    }
  }

  if(!final) def += 'FINISH_TRUE\n'

  const OBJECT_COPY_PREAMBLE = `
let cloned_params = null
function c(src) {
  if(!src || typeof src != "object") return src
  if(src instanceof Array) return src.map(x=>c(x))
  if(src instanceof Date) return new Date(src.getTime())
  if(src instanceof ArrayBuffer || ArrayBuffer.isView(src)) return src.slice()
  if(/^\\[object (Function|Error|RegExp|Window|HTML.*)\\]$/.test(Object.prototype.toString.call(src))) return src
  return Object.fromEntries(Object.entries(src).map(([k,v])=>[k,c(v)]))
}
function s(){
  if(!cloned_params) {
    cloned_params = c(params||{})
  }
}
function r(result){
  if(result) result = {...(params||{})}
  if(cloned_params && params){
    for(let p in params) delete params[p]
    for(let p in cloned_params) params[p]=cloned_params[p]
  }
  return result
}\n\n`

  if(targettxt_needed) def = 'const targettxt = (target && typeof(target)==\'object\') ? ((target.scope==\'~\' ? (target.instance_id===null || target.instance_id===undefined ? \'\' : target.instance_id) + \'~\' : target.scope ? (target.app_id===null || target.app_id===undefined ? \'\' : target.app_id) + target.scope : \'\') + target.listener_id + (target.request_id===null || target.request_id===undefined || String(target.request_id).length==0 ? \'\' : (\'$\'+String(target.request_id)))) : String(target);\n' + def
  if(sendertxt_needed) def = 'const sendertxt = (sender && typeof(sender)==\'object\') ? ((sender.scope==\'~\' ? (sender.instance_id===null || sender.instance_id===undefined ? \'\' : sender.instance_id) + \'~\' : sender.scope ? (sender.app_id===null || sender.app_id===undefined ? \'\' : sender.app_id) + sender.scope : \'\') + sender.listener_id + (sender.request_id===null || sender.request_id===undefined || String(sender.request_id).length==0 ? \'\' : (\'$\'+String(sender.request_id)))) : String(sender);\n' + def

  if(mutation_can_occur) {
    def = OBJECT_COPY_PREAMBLE + def.replace(/FINISH_FALSE/g, 'return r(false)').replace(/FINISH_TRUE/g, 'return r(true)')
  }else{
    def = def
      .replace(/FINISH_FALSE/g, 'return')
      .replace(/FINISH_TRUE/g, 'return(params || {})')
      .replace(/\s*\{ s\(\);(.*)\}\n/gm, '$1\n')
  }
  // eslint-disable-next-line no-new-func
  const fcn = new Function('sender', 'target', 'action', 'params', def)
  fcn.definition = def
  return fcn
} // createFilterFunction
